import { ResumeExperience } from '../../types';
import { Plus, X } from 'lucide-react';

interface ExperienceSectionProps {
    experience: ResumeExperience[];
    onChange: (experience: ResumeExperience[]) => void;
    maxBulletLength?: number;
    onOptimizeBullet: (jobIndex: number, bulletIndex: number, bulletText: string) => void;
    optimizingBulletKey?: string | null;
}

export function ExperienceSection({
    experience,
    onChange,
    maxBulletLength = 0,
    onOptimizeBullet,
    optimizingBulletKey = null,
}: ExperienceSectionProps) {
    const addJob = () => {
        onChange([
            ...experience,
            {
                id: Date.now().toString(),
                company: '',
                position: '',
                startDate: '',
                current: false,
                bullets: [],
            },
        ]);
    };

    const removeJob = (index: number) => {
        onChange(experience.filter((_, i) => i !== index));
    };

    const updateJob = (index: number, field: keyof ResumeExperience, value: any) => {
        const newExperience = [...experience];
        newExperience[index] = { ...newExperience[index], [field]: value };
        onChange(newExperience);
    };

    const addBullet = (jobIndex: number) => {
        const newExperience = [...experience];
        newExperience[jobIndex].bullets.push('');
        onChange(newExperience);
    };

    const removeBullet = (jobIndex: number, bulletIndex: number) => {
        const newExperience = [...experience];
        newExperience[jobIndex].bullets = newExperience[jobIndex].bullets.filter((_, i) => i !== bulletIndex);
        onChange(newExperience);
    };

    const updateBullet = (jobIndex: number, bulletIndex: number, value: string) => {
        const newExperience = [...experience];
        newExperience[jobIndex].bullets[bulletIndex] = value;
        onChange(newExperience);
    };

    return (
        <div className="card">
            <div className="flex justify-between items-center mb-4">
                <h2 className="section-title mb-0">Work Experience</h2>
                <button onClick={addJob} className="btn btn-secondary text-sm flex items-center gap-1">
                    <Plus size={16} />
                    Add Job
                </button>
            </div>

            <div className="space-y-6">
                {experience.map((job, jobIndex) => (
                    <div key={job.id} className="border border-gray-200 rounded-lg p-4 relative">
                        <button
                            onClick={() => removeJob(jobIndex)}
                            className="absolute top-2 right-2 text-red-600 hover:text-red-700"
                        >
                            <X size={20} />
                        </button>

                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="label">Position *</label>
                                    <input
                                        type="text"
                                        value={job.position}
                                        onChange={(e) => updateJob(jobIndex, 'position', e.target.value)}
                                        className="input"
                                        placeholder="Software Engineer"
                                    />
                                </div>
                                <div>
                                    <label className="label">Company *</label>
                                    <input
                                        type="text"
                                        value={job.company}
                                        onChange={(e) => updateJob(jobIndex, 'company', e.target.value)}
                                        className="input"
                                        placeholder="Tech Corp"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="label">Location</label>
                                <input
                                    type="text"
                                    value={job.location || ''}
                                    onChange={(e) => updateJob(jobIndex, 'location', e.target.value)}
                                    className="input"
                                    placeholder="San Francisco, CA"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="label">Start Date</label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]{4}-[0-9]{2}"
                                        placeholder="YYYY-MM"
                                        value={job.startDate}
                                        onChange={(e) => updateJob(jobIndex, 'startDate', e.target.value)}
                                        className="input"
                                    />
                                </div>
                                <div>
                                    <label className="label">End Date</label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]{4}-[0-9]{2}"
                                        placeholder="YYYY-MM"
                                        value={job.endDate || ''}
                                        onChange={(e) => updateJob(jobIndex, 'endDate', e.target.value)}
                                        className="input"
                                        disabled={job.current}
                                    />
                                </div>
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id={`current-${job.id}`}
                                    checked={job.current}
                                    onChange={(e) => updateJob(jobIndex, 'current', e.target.checked)}
                                    className="mr-2"
                                />
                                <label htmlFor={`current-${job.id}`} className="text-sm text-gray-700">
                                    I currently work here
                                </label>
                            </div>

                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <label className="label mb-0">Responsibilities & Achievements</label>
                                    <button
                                        onClick={() => addBullet(jobIndex)}
                                        className="text-sm text-primary-600 hover:text-primary-700"
                                    >
                                        + Add Bullet
                                    </button>
                                </div>

                                {job.bullets.map((bullet, bulletIndex) => {
                                    const isTooLong = maxBulletLength > 0 && bullet.length > maxBulletLength;
                                    const bulletKey = `${jobIndex}:${bulletIndex}`;
                                    return (
                                        <div key={bulletIndex} className="mb-2">
                                            <div className="flex gap-2">
                                                <textarea
                                                    value={bullet}
                                                    onChange={(e) => updateBullet(jobIndex, bulletIndex, e.target.value)}
                                                    className={`input flex-1 min-h-[60px] ${isTooLong ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
                                                    placeholder="Describe your achievement or responsibility..."
                                                />
                                                <div className="flex flex-col gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => onOptimizeBullet(jobIndex, bulletIndex, bullet)}
                                                        className="btn btn-secondary text-xs px-2 py-1"
                                                        disabled={optimizingBulletKey === bulletKey || !bullet.trim()}
                                                    >
                                                        {optimizingBulletKey === bulletKey ? 'Optimizing...' : 'Optimize'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeBullet(jobIndex, bulletIndex)}
                                                        className="text-red-600 hover:text-red-700 h-10"
                                                    >
                                                        <X size={20} />
                                                    </button>
                                                </div>
                                            </div>
                                            {isTooLong && (
                                                <p className="text-xs text-red-500 mt-1 text-right">
                                                    {bullet.length}/{maxBulletLength} characters
                                                </p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                ))}

                {experience.length === 0 && (
                    <p className="text-gray-500 text-center py-4">No work experience added yet</p>
                )}
            </div>
        </div>
    );
}
